import { Component, inject, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DaisyTooltipDirective } from '@pathfinder/daisy-controls/ui';
import { RouterLinkSafeUrlPipe, TimeAgoPipe } from '@lib/shared/util';
import { Router, RouterModule } from '@angular/router';
import { NotificationService } from '@lib/notification/data-access';
import { NotificationAction, NotificationModel } from '@lib/notification/models';
import { ToastMessageService } from '@lib/shared/data-access';
import { AuthService } from '@lib/auth/data-access';

@Component({
  selector: 'lib-notification-feature-link-notification-template',
  standalone: true,
  imports: [
    CommonModule,
    TimeAgoPipe,
    RouterModule,
    RouterLinkSafeUrlPipe,
    DaisyTooltipDirective,
  ],
  templateUrl: './link-notification-template.component.html',
  styleUrl: './link-notification-template.component.scss',
})
export class LinkNotificationTemplateComponent  {
  // inject
  private router = inject(Router);
  private notificationService = inject(NotificationService);
  private toastMessageService = inject(ToastMessageService);
  private authService = inject(AuthService);
  
  // public
  @Input() notification!: NotificationModel;
  public errorStatuses = [ NotificationAction.Errored, NotificationAction.BatchErrored ];

  // accessibility only --start--
  public onHandleNotificationsKeyDown(notifications: NotificationModel[] | NotificationModel, event: KeyboardEvent) {
    if (event.key === 'Enter') {
      // Check if notifications is a single object and wrap it in an array if necessary
      const notificationsArray = Array.isArray(notifications) ? notifications : [notifications];
      this.onOpenLinkClick(notificationsArray[0]);  // Navigate using the parent group notification or individual notification
      this.onMarkNotificationsReadStatusesClick(notificationsArray, true);
    }
  }
  // accessibility only --end--

  public onOpenLinkClick(notification: NotificationModel) {
    this.notificationService.hideNotificationSidebar();
    const route = this.buildRouteFromNotification(notification);
    this.router.navigateByUrl(route);
    this.onMarkNotificationsReadStatusesClick([notification], true);
  }

  public onHyperLinkClick(notification: NotificationModel, $event: MouseEvent) {
    if ($event) $event.stopPropagation();

    this.onMarkNotificationsReadStatusesClick(notification, true, $event);
  }

  public onMarkNotificationsReadStatusesClick(notifications: NotificationModel[] | NotificationModel, markAsRead: boolean, event?: MouseEvent) {
    if (event) event.stopPropagation();

    // Ensure notifications is always treated as an array
    const notificationsArray = Array.isArray(notifications) ? notifications : [notifications];

    // Filter out any notifications without an id and update their read status
    const updatedNotifications = notificationsArray.filter(notification => notification.id !== undefined);

    updatedNotifications.forEach(notification => {
      notification.isRead = markAsRead;
    });

    if (updatedNotifications.length > 0) {
      this.notificationService.updateNotificationsStatus(updatedNotifications).subscribe({
        error: () => this.toastMessageService.showError("Error updating notification status.")
      });
    }
  }

  public buildRouteFromNotification(notification: NotificationModel): string {
    if (notification.action === NotificationAction.ReportRendered) {
        return `/t/${this.authService.userPrincipalComputedSignal()?.currentTenantExternalId}/report-library/${notification.relatedModule?.moduleId}`;
    }
    if (notification.action === NotificationAction.BatchReportRendered) {
        return `/t/${this.authService.userPrincipalComputedSignal()?.currentTenantExternalId}/report-library/batch/${notification.relatedModule?.moduleId}`;
    }
    return "";
    }
}

